/**
 * Budget pipeline navigation — the Reconciliation Rail.
 *
 * The single nav for the budgeting workspace: five numbered stages
 * (Seed → Import → Reconcile → Review → Export) that double as live status.
 * Replaces the old 6 sub-tabs + wizard nesting (depth 4 → 2). Heritage-blue
 * chrome, future-blue progress accent. Collapses to an icon spine ≤1280px.
 */
import React from 'react';
import { cn } from '@/lib/utils';

export interface RailSegment { color: string; pct: number; }

export interface RailStage {
  id: string;
  label: string;
  icon: React.ReactNode;
  status?: string;
  /** Optional mini-waterfall shown under the node (Reconcile progress). */
  segments?: RailSegment[];
}

interface BudgetRailProps {
  stages: RailStage[];
  active: string;
  onSelect: (id: string) => void;
  footer?: React.ReactNode;
  recommendedNext?: string;
}

export default function BudgetRail({ stages, active, onSelect, footer, recommendedNext }: BudgetRailProps) {
  return (
    <nav
      aria-label="Budgeting pipeline"
      className="flex shrink-0 flex-col gap-1 rounded-xl bg-heritage-blue p-2.5 lg:w-[200px] w-16"
    >
      <div className="hidden px-2 pb-2 pt-1 text-[10px] uppercase tracking-[0.1em] text-heritage-blue-200 lg:block">
        Budget · pipeline
      </div>
      {stages.map((s, i) => {
        const on = s.id === active;
        const isNext = !on && s.id === recommendedNext;
        return (
          <button
            key={s.id}
            onClick={() => onSelect(s.id)}
            aria-current={on ? 'page' : undefined}
            title={`${s.label}${s.status ? ` — ${s.status}` : ''}`}
            className={cn(
              'group flex w-full items-start gap-2.5 border-l-[3px] border-transparent px-2 py-2 text-left transition-colors',
              on ? 'border-l-future-blue bg-future-blue/15' : 'hover:bg-white/[0.06]',
            )}
          >
            <span
              className={cn(
                'mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] tabular-nums',
                on ? 'bg-future-blue text-heritage-blue-900' : 'bg-white/10 text-heritage-blue-100',
              )}
            >
              {i + 1}
            </span>
            <span className="min-w-0 flex-1 hidden lg:block">
              <span className={cn('block font-serif text-sm leading-tight', on ? 'text-white' : 'text-heritage-blue-50')}>
                {s.label}
              </span>
              {s.status && (
                <span className={cn('mt-0.5 block text-[11px] tabular-nums leading-tight', on ? 'text-heritage-blue-100' : 'text-heritage-blue-300')}>
                  {s.status}
                </span>
              )}
              {s.segments && s.segments.length > 0 && (
                <span className="mt-1.5 flex h-1 w-full overflow-hidden rounded-sm" aria-hidden="true">
                  {s.segments.map((seg, j) => (
                    <span key={j} style={{ width: `${seg.pct}%`, background: seg.color }} />
                  ))}
                </span>
              )}
            </span>
            <span className="text-heritage-blue-100 lg:hidden" aria-hidden="true">{s.icon}</span>
            {isNext && <span className="ml-auto hidden h-1.5 w-1.5 shrink-0 self-center rounded-full bg-future-blue lg:block" aria-hidden="true" />}
          </button>
        );
      })}
      {footer && (
        <div className="mt-auto hidden border-t border-white/10 px-2 pt-2 lg:block">{footer}</div>
      )}
    </nav>
  );
}
